/* ── LiteZupp shared search matcher ───────────────────────────────────────────
   ONE matcher, used by the header popup (global.js) and the /search results page
   (templates/search.liquid). They used to disagree: the popup did an exact
   lookup against xref-index.json while /search rendered Shopify's native
   typo-tolerant results, which also search body_html — so a kit whose parts list
   mentions "1157" outranked the actual 1157 bulb, and "LH57" found nothing at
   all because no part code was indexed.

   An authoritative tier runs first — the first of these to produce anything wins:
     1. exact part code      LH57R-F  -> lh57r-f
     2. exact OE number      1157     -> l57, ls57, lr57, lh57r-f, lh57r-ft
     3. part-code prefix     LH57     -> lh57r-f, lh57r-ft
     4. OE-number prefix     115      -> 1156, 1157 ...

   Then title matches are appended, which both broadens a hit ("festoon" must
   reach BOTH festoon products, not just the one whose handle starts with it)
   and covers the 24 flashers/accessories that carry no cross_reference at all.

   The invariant Jim cared about: an all-digits query that already has an
   authoritative hit will NOT pick up kits from the title pass, so "1157" can
   never surface a kit whose parts list happens to mention a 1157 bulb. Kits
   sort last regardless. No typo tolerance — a wrong part number should say
   "no results", not confidently show the wrong bulb.
   ─────────────────────────────────────────────────────────────────────────── */
(function (w) {
  'use strict';

  // Must stay byte-for-byte identical to norm() in scripts/compile-xref.mjs.
  function normQ(s) { return String(s).toUpperCase().replace(/[^A-Z0-9]/g, ''); }

  function collect(map, handles, seen, limit, test) {
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length && handles.length < limit; i++) {
      if (!test(keys[i])) continue;
      var hs = map[keys[i]];
      for (var j = 0; j < hs.length && handles.length < limit; j++) {
        if (!seen[hs[j]]) { seen[hs[j]] = 1; handles.push(hs[j]); }
      }
    }
  }

  /**
   * @param {object} idx  parsed xref-index.json
   * @param {string} raw  what the user typed
   * @param {number} limit max handles to return (default 12)
   * @returns {{handles: string[], tier: string}} tier: 'code' | 'number' | 'title' | ''
   */
  function match(idx, raw, limit) {
    limit = limit || 12;
    var out = { handles: [], tier: '' };
    if (!idx) return out;

    var text = String(raw || '').trim();
    var q = normQ(text);
    if (q.length < 2) return out;

    var byCode = idx.byCode || {};
    var byNumber = idx.byNumber || {};
    var seen = {}, h = out.handles;

    var isExact = function (k) { return k === q; };
    var isPrefix = function (k) { return k.indexOf(q) === 0; };

    /* Part code before OE number: a customer typing "L57" means the LiteZupp
       part, not the OE number 57. Exact before prefix, so "L56" resolves to the
       one L56 rather than L56 + L56-M30. */
    var TIERS = [
      ['code', byCode, isExact, true],
      ['number', byNumber, isExact, true],
      ['code', byCode, isPrefix, false],
      ['number', byNumber, isPrefix, false],
    ];
    var wasExact = false;
    for (var t = 0; t < TIERS.length && !h.length; t++) {
      collect(TIERS[t][1], h, seen, limit, TIERS[t][2]);
      if (h.length) { out.tier = TIERS[t][0]; wasExact = TIERS[t][3]; }
    }

    /* Title pass. Skipped for 1-2 char scraps so a stray "56" doesn't
       substring-match every 1156 title, and skipped after an exact part-code
       hit: a part code names one product, so "LH57R-F" must not also drag in
       LH57R-FT. An exact OE number is different — it names a fitment family, so
       the title pass is allowed to complete it (LS56/L56-M30 say "1156" in
       their titles but omit it from cross_reference). */
    if (text.length >= 3 && h.length < limit && !(out.tier === 'code' && wasExact)) {
      var authoritative = h.length > 0;
      // Protects an OE-number hit from picking up a kit that merely mentions it.
      var dropKits = authoritative && /^[0-9]+$/.test(q);
      var needle = text.toLowerCase();
      var prods = idx.products || {};
      var keys = Object.keys(prods);
      var added = 0;
      for (var i = 0; i < keys.length && h.length < limit; i++) {
        var hk = keys[i], p = prods[hk];
        if (seen[hk] || !p) continue;
        if (p.t.toLowerCase().indexOf(needle) === -1) continue;
        if (dropKits && p.k) continue;
        seen[hk] = 1; h.push(hk); added++;
      }
      // A word like "festoon" prefix-matches a handle, but it's really a title
      // search — don't mislabel it "Part number matches".
      if (h.length && (!out.tier || (added && !wasExact))) out.tier = 'title';
    }
    return finish(idx, out);
  }

  // Kits last: a bulb search that legitimately catches a kit should still lead
  // with the bulb. Stable within each group.
  function finish(idx, out) {
    var prods = idx.products || {};
    var bulbs = [], kits = [];
    out.handles.forEach(function (hk) { (prods[hk] && prods[hk].k ? kits : bulbs).push(hk); });
    out.handles = bulbs.concat(kits);
    return out;
  }

  var LABELS = {
    code: 'Part number matches',
    number: 'Bulb / cross-reference matches',
    title: 'Products',
  };

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function money(n) { return n == null ? '' : '$' + Number(n).toFixed(2); }

  /* Full product card, markup-identical to snippets/product-card.liquid so the
     /search grid is indistinguishable from a collection grid. Keep the two in
     sync — the CSS lives in product-card.liquid's {% stylesheet %} block. */
  function card(p, cartUrl) {
    var img = p.i
      ? '<img src="' + esc(p.i) + '" alt="' + esc(p.t) + '" width="288" height="270" loading="lazy">'
      : '<span class="prod-img-ph" aria-hidden="true"></span>';
    var badge = p.so ? '<span class="badge gray">Sold Out</span>' : '';
    return '<article class="prod">' +
      '<a href="' + esc(p.u) + '" class="prod-img">' + img + badge + '</a>' +
      '<div class="prod-body">' +
        '<div class="prod-head">' +
          '<h3 class="prod-title"><a href="' + esc(p.u) + '">' + esc(p.t) + '</a></h3>' +
          /* A hard-coded 4.9 star rating used to print here on every card. It was
             never real — no product has a review. It was removed from the
             collection card before launch and this copy was missed. Showing a
             review score you did not collect is an FTC problem, not a cosmetic
             one. Judge.me fills this in from actual reviews and renders nothing
             until there is at least one. */
          '<div class="jdgm-widget jdgm-preview-badge" data-id="' + esc(p.id || '') + '"></div>' +
        '</div>' +
        (p.d ? '<p class="prod-desc">' + esc(p.d) + '</p>' : '') +
        '<div class="buy">' +
          '<span class="price">' +
            (p.pv ? '<span class="price-from">From</span> ' : '') + money(p.p) +
          '</span>' +
          /* `mv` = the product has options, so it must not be bought from the grid.
             The index omits a variant id entirely in that case. See the note in
             snippets/product-card.liquid — this markup must stay in step with it. */
          (p.mv
            ? '<a class="cart cart--choose" href="' + esc(p.u) + '">Choose options</a>'
            : p.v
              ? '<form method="post" action="' + esc(cartUrl || '/cart/add') + '">' +
                  '<input type="hidden" name="id" value="' + p.v + '">' +
                  '<input type="hidden" name="quantity" value="1">' +
                  '<button class="cart" type="submit" aria-label="Add ' + esc(p.t) + ' to cart"' + (p.so ? ' disabled' : '') + '>' +
                    '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 18a2 2 0 1 0 2 2 2 2 0 0 0-2-2zm10 0a2 2 0 1 0 2 2 2 2 0 0 0-2-2zM7.16 14h9.45a1 1 0 0 0 .87-.5l3.58-6.49A1 1 0 0 0 20.21 5H5.21l-.94-2H1v2h2l3.6 7.59-1.35 2.44A2 2 0 0 0 7 18h12v-2H7.16z"/></svg>' +
                  '</button>' +
                '</form>'
              : '') +
        '</div>' +
      '</div>' +
    '</article>';
  }

  w.LZSearch = {
    normQ: normQ,
    match: match,
    card: card,
    esc: esc,
    money: money,
    label: function (t) { return LABELS[t] || ''; },
  };
})(window);
